import * as RemixReact from "@remix-run/react";
import React from "react";
import { AnalyticsContext } from "~/contexts/analyticsContext";
import { AppContext } from "~/contexts/appContext";
import { AuthenticationContext } from "~/contexts/authenticationContext";
import { IdentityClaims } from "~/domain/tokens";
import * as Analytics from "~/hooks/useAnalytics";
import { usePlatformAuthentication } from "~/hooks/usePlatformAuthenticated";

export const useClerkAuthEnabled = () => {
  const { currentApp } = React.useContext(AppContext);

  const isClerkAuthEnabled = true;

  // Bypass Clerk Auth for Connect API and just use Vendr Platform Auth due to Clerk Auth cookies SameSite = "Lax" setting
  // not being allowed in an iframe being used by an external partner domain
  return currentApp === "connect-api" ? false : isClerkAuthEnabled;
};

type AuthenticatedArgs = {
  onAuthenticated?: (
    identityClaims: IdentityClaims,
    isClerkAuthEnabled: boolean,
  ) => void;
  onAuthInProgress?: () => void;
  onAuthenticationFailed?: () => void;
};

export const useAuthenticated = (args?: AuthenticatedArgs) => {
  const props = args ?? {};

  const revalidator = RemixReact.useRevalidator();
  const [notifiedFullyAuthenticated, setNotifiedFullyAuthenticated] =
    React.useState(false);

  const { anonymousId } = React.useContext(AnalyticsContext);
  const { identity, identityV2 } = React.useContext(AuthenticationContext);
  const { identify, group, alias, track } = Analytics.useAnalytics();

  const isClerkAuthEnabled = useClerkAuthEnabled();
  const isAuthenticatedWithClerk = identityV2?.type === "clerk";

  const onAuthenticated = props.onAuthenticated;

  const {
    frameRef,
    isAuthenticatedWithVendrPlatform,
    authenticationInProgress,
  } = usePlatformAuthentication({
    onCodeAuthInProgress: props.onAuthInProgress,
    onGoogleAuthInProgress: props.onAuthInProgress,
    onSamlAuthInProgress: props.onAuthInProgress,
    onCodeAuthFailed: props.onAuthenticationFailed,
    onGoogleAuthFailed: props.onAuthenticationFailed,
    onSamlAuthFailed: props.onAuthenticationFailed,
    onRedirectIframe: React.useCallback(
      ({
        frame,
        redirectTo,
      }: {
        frame: React.RefObject<HTMLIFrameElement>;
        redirectTo: string;
      }) => {
        if (frame.current && frame.current.src !== redirectTo) {
          frame.current.src = redirectTo;
        }
      },
      [],
    ),
  });

  const onFullyAuthenticated = React.useCallback(
    (identityClaims: IdentityClaims) => {
      onAuthenticated?.(identityClaims, isClerkAuthEnabled);
    },
    [onAuthenticated, isClerkAuthEnabled],
  );

  React.useEffect(() => {
    const isFullyAuthenticated = !!identity;
    const isRevalidating = revalidator.state === "loading";

    if (
      (isAuthenticatedWithClerk || isAuthenticatedWithVendrPlatform) &&
      !isFullyAuthenticated &&
      !isRevalidating
    ) {
      revalidator.revalidate();
      return;
    }

    if (identity && identityV2 && !notifiedFullyAuthenticated) {
      if (
        identityV2?.type === "clerk" &&
        identityV2.user.platform?.id &&
        identityV2.organization?.platform?.id
      ) {
        // Link anonymous session to new Clerk ID
        alias(identityV2.user.clerk.id, anonymousId);

        // Link old IZ ID to new Clerk ID
        alias(identityV2.user.clerk.id, identityV2.user.platform.id);

        // Group old IZ org to new Clerk org
        group({
          clerkOrganizationId: identityV2.organization.clerk.id,
          izOrganizationId: identityV2.organization.platform.id,
          name: identityV2.organization.name,
          domain: identityV2.organization.clerk.domain,
        });

        track({
          type: "Login",
          properties: {
            identity: {
              userId: identityV2.user.clerk.id,
              anonymousId: anonymousId ?? null,
              email: identityV2.user.email,
            },
          },
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        identity.userId && alias(identity.userId, anonymousId);
      }

      identify(identity);

      onFullyAuthenticated(identity);
      setNotifiedFullyAuthenticated(true);
    }
  }, [
    identity,
    identityV2,
    group,
    onFullyAuthenticated,
    revalidator,
    notifiedFullyAuthenticated,
    isAuthenticatedWithVendrPlatform,
    isAuthenticatedWithClerk,
    alias,
    identify,
  ]);

  return {
    authenticationFrameRef: frameRef,
    isAuthenticated: !!identity,
    authenticationInProgress,
  };
};

// Helper component to ensure the useAuthenticated hook is always initialized
export const AuthenticationInit = () => {
  useAuthenticated();

  return null;
};
